import { useState } from "react";
import { HelpCircle, Sparkles, Award, Rocket } from "lucide-react";
import { Button } from "./ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "./ui/dialog";

const TIERS = [
  { label: "Onboarding (primi 45gg)", pct: "2%", tone: "bg-indigo-50 border-indigo-200 text-indigo-900", icon: Rocket },
  { label: "0 – 4★ Fedeltà (senza piano)", pct: "6,0%", tone: "bg-slate-50 border-slate-200 text-slate-800", icon: null },
  { label: "5★ Fedeltà (−15% sulla commissione)", pct: "5,1%", tone: "bg-amber-50 border-amber-200 text-amber-900", icon: Award },
  { label: "Piano Basic (€29,99/mese)", pct: "4%", tone: "bg-emerald-50 border-emerald-200 text-emerald-900", icon: Sparkles },
  { label: "Piano Pro (€99,99/mese)", pct: "2,5%", tone: "bg-emerald-50 border-emerald-200 text-emerald-900", icon: Sparkles },
  { label: "Piano Enterprise (€299/mese)", pct: "1%", tone: "bg-emerald-100 border-emerald-300 text-emerald-900", icon: Sparkles },
];

const STARS = [
  { count: "0", stars: "☆", requirement: "Nessuna consegna" },
  { count: "1 – 4", stars: "★", requirement: "Prima consegna" },
  { count: "5 – 14", stars: "★★", requirement: "Fornitore attivo" },
  { count: "15 – 39", stars: "★★★", requirement: "Fornitore consolidato" },
  { count: "40 – 99", stars: "★★★★", requirement: "Fornitore top" },
  { count: "≥ 100", stars: "★★★★★", requirement: "Sconto commissione 15%" },
];

/**
 * Trigger button + modal that explains loyalty tiers and commission math.
 * Same content for both restaurants and suppliers (differ only in tone).
 */
export default function LoyaltyInfoDialog({ role = "supplier", triggerClassName = "" }) {
  const [open, setOpen] = useState(false);
  const isSupplier = role === "supplier";
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          type="button"
          className={`inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-widest text-slate-500 hover:text-slate-900 underline decoration-dotted underline-offset-4 ${triggerClassName}`}
          data-testid="loyalty-info-trigger"
        >
          <HelpCircle className="w-3.5 h-3.5" /> Come funziona la commissione?
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl" data-testid="loyalty-info-dialog">
        {!isSupplier ? (
          <div className="py-6 text-center" data-testid="buyer-no-commission">
            <DialogHeader>
              <DialogTitle className="font-display text-3xl sm:text-4xl text-slate-900">Sei acquirente? Nessuna commissione!</DialogTitle>
            </DialogHeader>
            <p className="mt-4 text-sm text-slate-600">Paghi esattamente il prezzo di aggiudicazione: la piattaforma si remunera solo con il fornitore.</p>
          </div>
        ) : (
        <>
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">
            {isSupplier ? "La tua commissione, spiegata" : "Come guadagniamo noi (e tu)"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5 text-sm text-slate-700">
          <p>
            {isSupplier
              ? "AstaAlimentare trattiene una piccola commissione sul prezzo della fornitura vinta. La commissione si abbassa man mano che consegni con successo: è il nostro modo di premiare chi lavora bene."
              : "Il fornitore vincitore paga alla piattaforma una piccola commissione sull'importo pattuito. Tu ricevi la merce al prezzo esatto dell'aggiudicazione — nessuna fee sopra."}
          </p>

          <div>
            <h4 className="font-display text-base font-bold text-slate-900 mb-2">Le stelle di fedeltà</h4>
            <p className="text-xs text-slate-600 mb-3">Contano solo gli ordini <b>consegnati</b> con successo — non le sole vittorie all'asta.</p>
            <div className="rounded-xl border border-slate-200 overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-slate-500 font-mono uppercase tracking-widest">
                  <tr>
                    <th className="px-3 py-2 text-left">Consegne</th>
                    <th className="px-3 py-2 text-left">Stelle</th>
                    <th className="px-3 py-2 text-left">Livello</th>
                  </tr>
                </thead>
                <tbody>
                  {STARS.map(s => (
                    <tr key={s.count} className="border-t border-slate-100">
                      <td className="px-3 py-2 font-mono">{s.count}</td>
                      <td className="px-3 py-2 text-amber-600">{s.stars}</td>
                      <td className="px-3 py-2">{s.requirement}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div>
            <h4 className="font-display text-base font-bold text-slate-900 mb-2">Commissione per tier</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {TIERS.map(t => {
                const Icon = t.icon;
                return (
                  <div key={t.label} className={`rounded-xl border p-3 ${t.tone}`}>
                    <div className="text-[10px] font-mono uppercase tracking-widest flex items-center gap-1">
                      {Icon && <Icon className="w-3 h-3" />} {t.label}
                    </div>
                    <div className="mt-1 font-display font-extrabold text-2xl">{t.pct}</div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="rounded-xl bg-slate-900 text-slate-100 p-4">
            <div className="text-[10px] font-mono uppercase tracking-widest text-slate-400 mb-2">Esempio · Ordine da €100</div>
            <div className="space-y-1 font-mono text-xs">
              <div className="flex justify-between"><span>Fornitore 3★</span><span>trattiene <b className="text-emerald-300">€94,00</b> · piattaforma <b>€6,00</b> (6%)</span></div>
              <div className="flex justify-between"><span>Fornitore 5★</span><span>trattiene <b className="text-emerald-300">€94,90</b> · piattaforma <b>€5,10</b> (5,1%)</span></div>
              <div className="flex justify-between"><span>Piano Basic</span><span>trattiene <b className="text-emerald-300">€96,00</b> · piattaforma <b>€4,00</b> (4%)</span></div>
              <div className="flex justify-between"><span>Piano Pro</span><span>trattiene <b className="text-emerald-300">€97,50</b> · piattaforma <b>€2,50</b> (2,5%)</span></div>
              <div className="flex justify-between"><span>Enterprise</span><span>trattiene <b className="text-emerald-300">€99,00</b> · piattaforma <b>€1,00</b> (1%)</span></div>
              <div className="flex justify-between"><span>Onboarding 45gg</span><span>trattiene <b className="text-emerald-300">€98,00</b> · piattaforma <b>€2,00</b> (2%) · min €1,50/ordine</span></div>
            </div>
          </div>

          <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4" data-testid="escrow-info-box">
            <h4 className="font-display text-base font-bold text-emerald-900 mb-1">Pagamento protetto · rilascio in 1h/6h</h4>
            <p className="text-xs text-emerald-900">
              {isSupplier
                ? "Quando segni un ordine come consegnato, il pagamento dell'acquirente resta trattenuto al massimo 1 ora (Express/Offerte) o 6 ore (asta), conteggiate nella fascia di consegna: l'acquirente può confermare subito (payout immediato) oppure contestare. Se non fa nulla entro il termine, la consegna viene confermata automaticamente e ricevi il payout."
                : "Dopo la consegna hai 1 ora (Express/Offerte) o 6 ore (asta), conteggiate nella tua fascia di consegna, per confermare la merce o contestarla. Trascorso il termine senza azioni, la consegna viene confermata automaticamente e il pagamento rilasciato al fornitore."}
            </p>
          </div>

          <p className="text-xs text-slate-500 italic">
            {isSupplier
              ? "Suggerimento: se lavori tanto, i piani Basic/Pro/Enterprise (da €29,99/mese) riducono la commissione fino all'1%."
              : "Suggerimento: i fornitori con piano Enterprise pagano solo l'1% di commissione, quindi possono offrire prezzi più aggressivi. Cerca il badge verde PREMIUM nelle offerte."}
          </p>
        </div>

        <div className="flex justify-end pt-2">
          <Button onClick={() => setOpen(false)} className="rounded-full" data-testid="loyalty-info-close">Chiudi</Button>
        </div>
        </>
        )}
      </DialogContent>
    </Dialog>
  );
}
